import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { setDocumentTitle } from "shared/util"
import {
	Container, ContainerProps, 
	Paper, Stack, Box, 
	Button, LinearProgress,
	Divider,
	Pagination
} from "@mui/material"
import { SearchIcon } from "shared/icons"
import {
	Pause as PauseIcon,
	PlayCircle as ResumeIcon
} from "@mui/icons-material"
import { useSideMenu } from "components/sideMenu/container"
import { useTopBar } from "components/topBar/container"
import { useTenant } from "features/tenant/context"
import { KojinPreviewDialog } from "features/kojin/dialog/preview"
import { SisetuPreviewDialog } from "features/sisetu/dialog/preview"

import { useMatching } from "./container"
import { MatchingRecord } from "./components/record"
import { MatchingFilterState } from './components/filterState'
import { MatchingConditionDialog } from "./dialog/condition"
import { MatchingCondition } from "./types"
import { ErrorBlock } from "components/controls/errorInfo"
import { ConfirmDialog } from "components/dialog/confirm"

type MatchingPageProps = {
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
} & ContainerProps
export const MatchingPage = ({
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage },
	...others
}: MatchingPageProps) => {
	const tenant = useTenant()
	const [modal, setModal] = useState<'search' | 'stopConfirm' | null>('search')
	const [downloadState, setDownloadState] = useState<'downloading' | 'downloaded' | null>(null)
	const refBody = useRef<HTMLDivElement>(null)

	const container = useMatching()
	const {
		doSearch, condition,
		getResultCsv, filteredList, uuidList,
		autoMatchingMode, autoMatchingIndex, 
		autoMatchingError, autoMatchingStop, autoMatchingResume,
		pageCount, page, setPage,
		openDcfCode, setOpenDcfCode
	} = container

	const validList = useMemo(
		() => {
			const start = (page - 1) * 100
			return filteredList.slice(start, start + 100)
		},
		[page, filteredList]
	)
	const closeModal = useCallback(
		() => setModal(null),
		[]
	)
	const onSearch = useCallback(
		() => {
			if (autoMatchingMode === 'matching') {
				autoMatchingStop()
				setModal("stopConfirm")
			} else {
				setModal("search")
			}
		},
		[setModal, autoMatchingMode, autoMatchingStop]
	)
	const onSubmit = useCallback(
		(param: MatchingCondition) => {
			doSearch(param)
			closeModal()
		},
		[doSearch, closeModal]
	)
	const onDownload = useCallback(
		async (target?: 'kojin' | 'sisetu', columns?: string[]) => {
			setDownloadState('downloading')
			const csv = getResultCsv(target, columns)
    	await navigator.clipboard.writeText(csv)
			setDownloadState('downloaded')
			setTimeout(() => setDownloadState(null), 3000)
		},
		[getResultCsv, setDownloadState]
	)
	useEffect(
		() => {
			if (refBody.current) {
      	refBody.current.scrollTop = 0
			}
		},
		[page, refBody]
	)

  useEffect(
    () => {
      setBreadCrumbs([{ label: '名寄せ機能' }])
      setDocumentTitle('名寄せ機能', tenant.tenantName)
			setActivePage('matching')
    },
    [tenant, setBreadCrumbs, setActivePage]
  )
	return (
		<Container
			{...others}

			maxWidth="xl"
			sx={{
				height: '100%',
				maxHeight: '100%',
				overflow: 'hidden',
				padding: 2,
				position: 'relative'
			}}
		>
			<Paper sx={{ height: '100%', overflow: 'hidden' }}>
				<Stack
					flexGrow={1}
					direction="column"
					maxHeight="100%"
					maxWidth="100%"
					height="100%"
					boxSizing="border-box"
				>
					<Stack direction="column" spacing={1} padding={1}>
						<Stack direction="row" spacing={1}>
							<Stack direction="row" spacing={1}>
								<Button
									startIcon={<SearchIcon />}
									variant="contained"
									onClick={onSearch}
								>
									名寄せ条件を指定
								</Button>
							</Stack>

							<Box flexGrow={1}/>

							<MatchingFilterState container={container} direction="row" spacing={1}/>

							{uuidList.length === 0 ? (
								<></>
							) : autoMatchingMode === 'matching' ? (
								<Button
									variant="contained"
									startIcon={<PauseIcon/>}
									color='error'
									onClick={() => autoMatchingStop()}
								>
									中断
								</Button>
							) : autoMatchingMode === 'stop' ? (
								<>
									<Button
										variant="contained"
										color="secondary"
										startIcon={<ResumeIcon/>}
										onClick={() => autoMatchingResume()}
									>
										再開
									</Button>

									<Button
										variant="contained"
										onClick={() => onDownload()}
									>
										{(
											downloadState === 'downloading' ? 'コピー中...' :
											downloadState === 'downloaded' ? 'コピーしました!' :
											condition?.target === 'kojin' ? 'DCF個人コードをコピー' :
											condition?.target === 'sisetu' ? 'DCF施設コードをコピー' :
											'--'
										)}
									</Button>
								</>
							) : autoMatchingMode === 'finish' ? (
								<>
									<Button
										variant="contained"
										onClick={() => onDownload()}
									>
										{(
											downloadState === 'downloading' ? 'コピー中...' :
											downloadState === 'downloaded' ? 'コピーしました!' :
											condition?.target === 'kojin' ? 'DCF個人コードをコピー' :
											condition?.target === 'sisetu' ? 'DCF施設コードをコピー' :
											'--'
										)}
									</Button>
									{/*condition?.target === 'kojin' ? (
										<Button
											variant="contained"
											onClick={() => onDownload('kojin', ['isiName', 'reason'])}
										>
											{(
												downloadState === 'downloading' ? 'コピー中...' :
												downloadState === 'downloaded' ? 'コピーしました!' :
												'医師情報をコピー'
											)}
										</Button>
									) : condition?.target === 'sisetu' ? (
										<Button
											variant="contained"
											onClick={() => onDownload('sisetu', ['sisetuName', 'todofuken'])}
										>
											{(
												downloadState === 'downloading' ? 'コピー中...' :
												downloadState === 'downloaded' ? 'コピーしました!' :
												'施設情報をコピー'
											)}
										</Button>
									) : <></>*/}
								</>
							) : <></>}
						</Stack>

						{uuidList.length > 0 && (autoMatchingMode === 'matching' || autoMatchingMode === 'stop') && (
							<Box height="4px">
								<LinearProgress variant="determinate" value={
									Math.floor((autoMatchingIndex * 100) / uuidList.length)
								}/>
							</Box>
						)}
					</Stack>

					<Divider/>

					<ErrorBlock error={autoMatchingError}/>

					<Stack
						ref={refBody}
						width="100%"
						flexGrow={1}
						overflow='auto'
						padding={1}
						boxSizing="border-box"
					>
						{validList.map((r, index) => (
							<MatchingRecord
								key={r.uuid}
								uuid={r.uuid}
								index={r.index}
								container={container}
								marginTop={index > 0 ? 1 : 0}
							/>
						))}
					</Stack>

					{pageCount > 1 && (
						<>
							<Divider/>
							<Stack direction="column" spacing={1} padding={1}>
								<Pagination
									count={pageCount}
									page={page}
									onChange={(_, value) => setPage(value)}
									shape="rounded"
								/>
							</Stack>
						</>
					)}
				</Stack>
			</Paper>

			{modal === 'stopConfirm' ? (
				<ConfirmDialog
					message="名寄せを中断します。よろしいですか？"
					ok={{
						label: 'はい',
						onClick: () => {
							setModal("search")
						}
					}}
					cancel={{
						label: 'いいえ',
						onClick: () => {
							setModal(null)
						}
					}}
				/>
			) : modal === 'search' ? (
				<MatchingConditionDialog
					condition={condition ?? undefined}
					onSubmit={onSubmit}
					onClose={closeModal}
				/>
			) : <></>}

			{openDcfCode ? (
				condition?.target === 'kojin' ? (
					<KojinPreviewDialog
						kojinCode={openDcfCode}
						onClose={() => setOpenDcfCode(null)}
					/>
				) : condition?.target === 'sisetu' ? (
					<SisetuPreviewDialog
						sisetuCode={openDcfCode}
						onClose={() => setOpenDcfCode(null)}
					/>
				) : (
					<></>
				)
			) : <></>}
		</Container>
	)
}

